// apps/provider/src/components/ai/CopilotPanel.jsx

import { useState, useEffect, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  X, Send, Minimize2, Maximize2, RotateCw,
  Sparkles, AlertCircle, Loader2, ChevronRight
} from 'lucide-react';
import { useCopilotStore } from '../../stores/copilotStore';
import { apiService } from '@/services/api';
import { Button } from '../ui/Button';
import { cn } from '@/lib/utils';
import { useCurrentPatient } from '@/contexts/CurrentPatientContext';
import { formatPatientName } from '@/utils/patientName';
import { usePatientNameFormat } from '@/hooks/usePatientNameFormat';

export function CopilotPanel({ onClose }) {
  const location = useLocation();
  const params = useParams();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const { currentPatient } = useCurrentPatient();
  const nameFormat = usePatientNameFormat();

  const {
    isMinimized,
    messages,
    isLoading,
    error,
    suggestions,
    showSuggestions,
    currentContext,
    toggleMinimized,
    sendMessage,
    clearMessages,
    setContext,
    generateSuggestions,
    toggleSuggestions
  } = useCopilotStore();

  // Fetch patient data if we have context - removed effect after adding current patient context

  // Update context when location or patient changes
  useEffect(() => {
    const context = {
      page: location.pathname,
      patientId: currentPatient?.id,
      patientName: currentPatient ? `${currentPatient.first_name} ${currentPatient.last_name}` : null,
      patientSummary: currentPatient?.patient_summary?.content || null,
      timestamp: new Date().toISOString()
    };

    setContext(context);
    generateSuggestions();
  }, [location.pathname, currentPatient]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle send message
  const handleSendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const message = input;
    setInput('');
    setIsTyping(false);

    try {
      await sendMessage(message);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion);
    inputRef.current?.focus();
  };

  // Get contextual quick actions based on current page
  const getQuickActions = () => {
    const path = location.pathname;

    if (path.includes('/encounters')) {
      return [
        'What are the recommended vital signs to check?',
        'Suggest differential diagnoses based on symptoms',
        'What follow-up tests should I consider?'
      ];
    } else if (path.includes('/clinical-notes')) {
      return [
        'Help me write the assessment section',
        'Suggest ICD-10 codes for this diagnosis',
        'Review my SOAP note for completeness'
      ];
    } else if (path.includes('/prescriptions')) {
      return [
        'Check drug interactions',
        'What is the standard dosage?',
        'Are there generic alternatives?'
      ];
    } else if (path.includes('/patients')) {
      return [
        'Summarize patient history',
        'What preventive care is due?',
        'Review medication adherence'
      ];
    }

    return [
      'What can you help me with?',
      'Show me tips for this page',
      'What are the keyboard shortcuts?'
    ];
  };

  const quickActions = getQuickActions();

  if (isMinimized) {
    return (
      <div className="fixed bottom-16 sm:bottom-24 right-3 sm:right-6 bg-white rounded-lg shadow-2xl z-40">
        <button
          onClick={toggleMinimized}
          className="flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 hover:bg-gray-50 transition-colors"
        >
          <Sparkles className="h-4 sm:h-5 w-4 sm:w-5 text-primary" />
          <span className="font-medium text-gray-700 text-sm sm:text-base">Chat</span>
          <Maximize2 className="h-3 sm:h-4 w-3 sm:w-4 text-gray-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-16 sm:bottom-24 right-3 sm:right-6 left-3 sm:left-auto w-auto sm:w-96 max-h-[70vh] sm:max-h-[600px] bg-white rounded-lg shadow-2xl z-40 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-medium text-gray-900">Chat</h3>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={clearMessages}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Clear chat"
          >
            <RotateCw className="h-4 w-4 text-gray-500" />
          </button>

          <button
            onClick={toggleMinimized}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Minimize"
          >
            <Minimize2 className="h-4 w-4 text-gray-500" />
          </button>

          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded transition-colors"
            title="Close"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>
      </div>

      {/* Context Bar */}
      {currentContext && (
        <div className="px-4 py-2 bg-gray-50 border-b">
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span>Context:</span>
            {currentPatient && (
              <span className="px-2 py-0.5 bg-white rounded border">
                {formatPatientName(currentPatient, nameFormat)}
              </span>
            )}
            <span className="px-2 py-0.5 bg-white rounded border">
              {location.pathname.split('/')[1] || 'Home'}
            </span>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h4 className="text-sm font-medium text-gray-600 mb-2">
              How can I assist you?
            </h4>
            <p className="text-xs text-gray-500 mb-6">
              I can help with clinical decisions, documentation, and workflow guidance.
            </p>

            {/* Quick Actions */}
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(action)}
                  className="w-full text-left px-3 py-2 text-xs sm:text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700 pr-2">{action}</span>
                    <ChevronRight className="h-3 sm:h-4 w-3 sm:w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex",
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    "max-w-[80%] rounded-lg px-4 py-2",
                    message.role === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-800'
                  )}
                >
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {new Date(message.timestamp).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-600" />
                    <span className="text-sm text-gray-600">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="flex justify-center">
                <div className="flex items-start gap-2 px-3 py-2 bg-red-50 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="px-4 py-2 border-t bg-gray-50">
          <div className="flex gap-2 overflow-x-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                className="flex-shrink-0 px-3 py-1 text-xs bg-white border rounded-full hover:bg-gray-100 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setIsTyping(e.target.value.length > 0);
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Ask me anything..."
            className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            disabled={isLoading}
          />

          <Button
            onClick={handleSendMessage}
            disabled={!input.trim() || isLoading}
            size="sm"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>

        {isTyping && (
          <p className="text-xs text-gray-500 mt-2">Press Enter to send</p>
        )}
      </div>
    </div>
  );
}